'use client';

import { useState } from 'react';
import styles from './ValuePromises.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { TeamCarousel } from '../TeamCarousel/TeamCarousel';

interface TeamMemberData {
  id: string;
  role: string;
  subtitle: string;
  icon: string;
  image?: string;
  imageAlt?: string;
}

interface Metric {
  label: string;
  value: string;
  unit?: string;
  trend?: 'up' | 'down' | 'neutral';
  highlighted?: boolean;
}

interface DashboardData {
  title: string;
  metrics: Metric[];
}

interface MetricCardData {
  key: string;
  label: string;
  headline: string;
  summary: string;
  status: 'ok' | 'warning' | 'info';
  details: { label: string; value: string }[];
}

interface PromiseData {
  number: string;
  label: string;
  title: string;
  highlightedTitle?: string;
  description: string;
  team?: TeamMemberData[];
  dashboard?: DashboardData;
  metricCards?: MetricCardData[];
  metricCardsTitle?: string;
}

export interface ValuePromisesProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  subtitle?: string;
  promises?: PromiseData[];
  teamBackgroundImage?: string;
  teamCaption?: string;
  illustrativeLabel?: string;
  lang?: string;
}

const DEFAULT_ILLUSTRATIVE_LABEL = (lang: string) =>
  lang === 'en'
    ? 'Example project dashboard — illustrative data.'
    : 'Exemple de dashboard projet — données illustratives.';

const DEFAULT_TEAM: TeamMemberData[] = [
  {
    id: 'web',
    role: 'Développeur senior',
    subtitle: 'Full-stack & cloud',
    icon: 'web',
    image: 'https://ik.imagekit.io/lindahestia/articles/role_developer_code_under_100kb.webp',
    imageAlt: 'Développeur senior',
  },
  {
    id: 'data',
    role: 'Data engineer',
    subtitle: 'Pipelines & modèles',
    icon: 'data',
    image: 'https://ik.imagekit.io/lindahestia/articles/role_data_analysis_under_100kb.webp',
    imageAlt: 'Data engineer',
  },
  {
    id: 'delivery',
    role: 'Delivery manager',
    subtitle: 'Votre interlocuteur unique',
    icon: 'delivery',
    image: 'https://ik.imagekit.io/lindahestia/articles/portrait-formation-averiane.webp',
    imageAlt: 'Delivery manager',
  },
  {
    id: 'product',
    role: 'Product owner',
    subtitle: 'Scope & priorité',
    icon: 'product',
    image: 'https://ik.imagekit.io/lindahestia/articles/role_strategy_sketch_under_100kb.webp',
    imageAlt: 'Product owner',
  },
  {
    id: 'agile',
    role: 'Coach agile',
    subtitle: 'Rituels & valeur',
    icon: 'agile',
    image: 'https://ik.imagekit.io/lindahestia/articles/role_facilitation_notes_under_100kb.webp',
    imageAlt: 'Coach agile',
  },
];

const DEFAULT_PROMISES: PromiseData[] = [
  {
    number: '01',
    label: 'Renforcer vos équipes',
    title: 'Une taskforce senior',
    highlightedTitle: 'à votre disposition',
    description:
      'Vivien et Soraya forment notre taskforce de base. Selon le périmètre, nous mobilisons les spécialistes qu’il faut : développement, data, produit et agilité. Vos interlocuteurs restent les mêmes, de la première discussion à la livraison.',
    team: DEFAULT_TEAM,
  },
  {
    number: '02',
    label: 'Visibilité en temps réel',
    title: 'L’avancée, les risques et les décisions',
    highlightedTitle: 'sur un seul écran',
    description:
      'Un responsable delivery rend compte chaque semaine de la progression, des risques et des décisions à prendre. Les indicateurs DORA et de delivery sont partagés avec vous dès le premier incrément.',
    metricCardsTitle: 'Tableau de bord delivery',
    metricCards: [
      {
        key: 'deploy-frequency',
        label: 'Deployment frequency',
        headline: '12 / semaine',
        summary: 'Livraisons fréquentes et maîtrisées grâce à une CI/CD stable et automatisée.',
        status: 'ok',
        details: [
          { label: 'Dernier run', value: '1m 24s' },
          { label: 'Env. recette', value: '3 auj.' },
          { label: 'Build CI', value: 'Pass' },
        ],
      },
      {
        key: 'lead-time',
        label: 'Lead time',
        headline: '2.4 j',
        summary: 'Temps moyen entre le premier commit et la mise en production.',
        status: 'info',
        details: [
          { label: 'PR ouvertes', value: '2' },
          { label: 'Temps moyen review', value: '4h' },
          { label: 'Approbations', value: '5/5' },
        ],
      },
      {
        key: 'failure-rate',
        label: 'Change failure rate',
        headline: '3.2 %',
        summary: 'Taux d’échec contrôlé avec un time to restore mesuré et actionnable.',
        status: 'warning',
        details: [
          { label: 'Time to restore', value: '45 min' },
          { label: 'Risques actifs', value: '3' },
          { label: 'Incidents', value: '0 critique' },
        ],
      },
    ],
  },
  {
    number: '03',
    label: 'Qualité mesurable',
    title: 'Des standards',
    highlightedTitle: 'partagés avec vous',
    description:
      'Les métriques de qualité sont choisies selon votre environnement, vos outils et la maturité du projet. Elles sont traçables, actionnables et jamais utilisées comme une façade.',
    metricCardsTitle: 'Qualité suivie en temps réel',
    metricCards: [
      {
        key: 'dependabot',
        label: 'Dependabot',
        headline: '0 open',
        summary: 'Dépendances surveillées et PR automatiques vérifiées pendant le sprint.',
        status: 'ok',
        details: [
          { label: 'Security alerts', value: '0' },
          { label: 'Version drift', value: '0 critical' },
          { label: 'Auto-merge', value: 'ready' },
        ],
      },
      {
        key: 'codeguard',
        label: 'CodeGuard',
        headline: '0 open',
        summary: 'Secrets, vulnérabilités et patterns risqués restent sous contrôle.',
        status: 'ok',
        details: [
          { label: 'Secrets', value: '0' },
          { label: 'SAST', value: 'clean' },
          { label: 'Time to repair', value: '2h' },
        ],
      },
      {
        key: 'sonar',
        label: 'Sonar',
        headline: 'Gate A',
        summary: 'Maintenabilité, duplication et couverture restent visibles à chaque ticket.',
        status: 'ok',
        details: [
          { label: 'Maintainability', value: 'A' },
          { label: 'Duplication', value: '1.1%' },
          { label: 'Coverage', value: '97.2%' },
        ],
      },
    ],
  },
];

function MetricsDashboard({
  data,
  illustrativeLabel,
}: {
  data: DashboardData;
  illustrativeLabel?: string;
}) {
  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardHeader}>
        <span className={styles.dashboardDot} />
        <span className={styles.dashboardTitle}>{data.title}</span>
      </div>
      <div className={styles.dashboardGrid}>
        {data.metrics.map((metric) => (
          <div
            key={metric.label}
            className={`${styles.dashboardCard} ${metric.highlighted ? styles.dashboardCardHighlighted : ''}`}
          >
            <div className={styles.dashboardCardHeader}>
              <span className={styles.dashboardLabel}>{metric.label}</span>
              {metric.trend && (
                <span
                  className={`${styles.dashboardTrend} ${
                    metric.trend === 'up'
                      ? styles.dashboardTrendUp
                      : metric.trend === 'down'
                        ? styles.dashboardTrendDown
                        : styles.dashboardTrendNeutral
                  }`}
                >
                  {metric.trend === 'up' ? '↑' : metric.trend === 'down' ? '↓' : '→'}
                </span>
              )}
            </div>
            <div className={styles.dashboardValueRow}>
              <span className={styles.dashboardValue}>{metric.value}</span>
              {metric.unit && <span className={styles.dashboardUnit}>{metric.unit}</span>}
            </div>
          </div>
        ))}
      </div>
      {illustrativeLabel && <p className={styles.illustrativeCaption}>{illustrativeLabel}</p>}
    </div>
  );
}

const DependabotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <rect x="5" y="7" width="14" height="11" rx="4" />
    <path d="M9 7V4h6v3" />
    <circle cx="10" cy="12.5" r="1" fill="currentColor" />
    <circle cx="14" cy="12.5" r="1" fill="currentColor" />
    <path d="M10 16h4" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SonarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <path d="M4 15c3-5 8-8 16-8" />
    <path d="M7 18c2-3 5-5 10-6" />
    <path d="M11 20c1.5-1.5 3-2.4 6-3" />
  </svg>
);

const DeployIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const LeadTimeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const FailureRateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const METRIC_ICONS: Record<string, React.ReactNode> = {
  dependabot: <DependabotIcon />,
  codeguard: <ShieldIcon />,
  sonar: <SonarIcon />,
  'deploy-frequency': <DeployIcon />,
  'lead-time': <LeadTimeIcon />,
  'failure-rate': <FailureRateIcon />,
};

function MetricCards({
  metrics,
  title,
  illustrativeLabel,
}: {
  metrics: MetricCardData[];
  title?: string;
  illustrativeLabel?: string;
}) {
  return (
    <div className={styles.metricCardsPanel}>
      {title && (
        <div className={styles.metricCardsPanelHeader}>
          <span className={styles.metricCardsPanelDot} />
          <span className={styles.metricCardsPanelTitle}>{title}</span>
        </div>
      )}
      <div className={styles.metricCardsGrid}>
        {metrics.map((metric) => (
          <article
            key={metric.key}
            className={`${styles.metricCard} ${styles[`metricCard_${metric.status}`]}`}
          >
            <div className={styles.metricCardTop}>
              <span className={styles.metricCardIcon} aria-hidden="true">
                {METRIC_ICONS[metric.key]}
              </span>
              <div className={styles.metricCardNameGroup}>
                <span className={styles.metricCardName}>{metric.label}</span>
                <span className={styles.metricCardStatus}>en temps réel</span>
              </div>
              <span className={styles.metricCardHeadline}>{metric.headline}</span>
            </div>
            <p className={styles.metricCardSummary}>{metric.summary}</p>
            <div className={styles.metricCardDetails}>
              {metric.details.map((detail) => (
                <span key={detail.label} className={styles.metricCardDetail}>
                  <span>{detail.label}</span>
                  <strong>{detail.value}</strong>
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
      {illustrativeLabel && <p className={styles.illustrativeCaption}>{illustrativeLabel}</p>}
    </div>
  );
}

export function ValuePromises({
  eyebrow = 'NOS 3 ENGAGEMENTS',
  title = 'Ce que nous mettons',
  highlightedTitle = '*en œuvre pour vous*',
  subtitle = 'Chaque promesse ci-dessus devient concrète dès le premier jour de collaboration.',
  promises = DEFAULT_PROMISES,
  teamBackgroundImage = 'https://ik.imagekit.io/lindahestia/articles/equipe-analyse-ecran.webp',
  teamCaption,
  illustrativeLabel,
  lang = 'fr',
}: ValuePromisesProps) {
  useAOS();
  const [activeIndex, setActiveIndex] = useState(0);
  const active = promises[activeIndex];
  const resolvedIllustrativeLabel = illustrativeLabel ?? DEFAULT_ILLUSTRATIVE_LABEL(lang);

  return (
    <section className={styles.section} aria-labelledby="value-promises-title">
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 id="value-promises-title" className={styles.title}>
            {title} <em>{highlightedTitle}</em>
          </h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        <div
          className={styles.tabs}
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          {promises.map((promise, index) => (
            <button
              key={promise.number}
              type="button"
              className={`${styles.tab} ${index === activeIndex ? styles.tabActive : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-selected={index === activeIndex}
              role="tab"
            >
              <span className={styles.tabNumber}>{promise.number}</span>
              <span className={styles.tabLabel}>{promise.label}</span>
            </button>
          ))}
        </div>

        <div className={`${styles.panel} ${styles.panelStacked}`} key={active.number}>
          <h3 className={styles.panelTitle}>
            {active.title} <em>{active.highlightedTitle}</em>
          </h3>

          {active.metricCards && (
            <MetricCards
              metrics={active.metricCards}
              title={active.metricCardsTitle}
              illustrativeLabel={resolvedIllustrativeLabel}
            />
          )}

          {active.team && (
            <div className={styles.teamCarouselWrapper}>
              <TeamCarousel
                members={active.team}
                backgroundImage={teamBackgroundImage}
                caption={teamCaption}
              />
            </div>
          )}

          {active.dashboard && (
            <MetricsDashboard
              data={active.dashboard}
              illustrativeLabel={resolvedIllustrativeLabel}
            />
          )}

          <p className={`${styles.panelDescription} ${styles.panelDescriptionCentered}`}>
            {active.description}
          </p>
        </div>
      </div>
    </section>
  );
}
